import dagre from 'dagre';
import { EDGE_TYPES } from './CanvasConstants';
import {
  COMPONENT_HANDLE_POSITION,
  DAGRE_DEFAULT_NODE_HEIGHT,
  DAGRE_DEFAULT_NODE_WIDTH,
  DAGRE_NODE_SEPARATION,
  DAGRE_RANK_SEPARATION,
  MIN_LAYOUT_NODE_HEIGHT,
  NODE_HEIGHT_PADDING,
} from './layoutConstants';
import useCanvasStore from '../components/pages/Canvas/useCanvasStore';

export const IGNORED_LINKS_FOR_DAGRE = [
  EDGE_TYPES.LOOPLINK,
  EDGE_TYPES.FORMREDIRECTLINK,
  EDGE_TYPES.NOTE_LINK,
];

const NOTE_NODE_TYPE = 'note';

// React Flow v11 stores measured dimensions directly on the node as
// `width`/`height` (not `measured.width/height` like v12). Treat any node
// with both as already-measured.
const hasLiveDims = node =>
  typeof node?.width === 'number' && typeof node?.height === 'number';

/**
 * Run dagre auto-layout on a set of react-flow nodes/edges.
 *
 * @param {Array} nodesItems - react-flow nodes to position
 * @param {Array} edges - react-flow edges
 * @param {Object} [options]
 * @param {{x:number,y:number}} [options.parentPosition] - offset added to every laid-out node
 * @param {boolean} [options.jitter] - add tiny random delta to x to force react-flow re-render
 * @returns {Array} positioned nodes + note nodes (untouched) + optimized edges
 */
export const applyDagreLayouting = (nodesItems, edges, options = {}) => {
  const { parentPosition = { x: 0, y: 0 }, jitter = false } = options;

  const DagreGraph = new dagre.graphlib.Graph();
  DagreGraph.setDefaultEdgeLabel(() => ({}));
  DagreGraph.setGraph({
    rankdir: 'LR',
    nodesep: DAGRE_NODE_SEPARATION,
    ranksep: DAGRE_RANK_SEPARATION,
    ranker: 'network-simplex',
    align: 'UL',
  });

  // Notes don't participate in layout but still come along in the output.
  const noteNodes = nodesItems.filter(node => node.type === NOTE_NODE_TYPE);
  const regularNodes = nodesItems.filter(node => node.type !== NOTE_NODE_TYPE);

  regularNodes.forEach(node => {
    DagreGraph.setNode(node.id, {
      width: resolveLayoutWidth(node),
      height: resolveLayoutHeight(node),
    });
  });

  edges.forEach(edge => {
    if (!IGNORED_LINKS_FOR_DAGRE.includes(edge.type)) {
      DagreGraph.setEdge(edge.source, edge.target);
    }
  });

  dagre.layout(DagreGraph);

  const positioned = regularNodes.map(node => {
    const dagreNode = DagreGraph.node(node.id);
    const handleOffset =
      node.type === NOTE_NODE_TYPE ? dagreNode.height / 2 : COMPONENT_HANDLE_POSITION;

    // Shift dagre's center anchor to top-left to match react-flow's anchor.
    return {
      ...node,
      targetPosition: 'left',
      sourcePosition: 'right',
      position: {
        x:
          dagreNode.x -
          dagreNode.width / 2 +
          parentPosition.x +
          (jitter ? Math.random() / 1000 : 0),
        y: dagreNode.y - handleOffset + parentPosition.y,
      },
    };
  });

  const optimizedEdges = optimizeEdgePaths(edges, positioned);

  return [...positioned, ...noteNodes, ...optimizedEdges];
};

const optimizeEdgePaths = (edges, nodes) => {
  const nodeMap = {};
  nodes.forEach(node => {
    nodeMap[node.id] = node;
  });

  return edges.map(edge => {
    if (IGNORED_LINKS_FOR_DAGRE.includes(edge.type)) {
      return edge;
    }

    const sourceNode = nodeMap[edge.source];
    const targetNode = nodeMap[edge.target];

    if (!sourceNode || !targetNode) {
      return edge;
    }

    const verticalDistance = Math.abs(targetNode.position.y - sourceNode.position.y);

    let edgeType = 'default';
    let curvature = 0;

    if (verticalDistance > 200) {
      edgeType = 'smoothstep';
      curvature = Math.min(0.5, verticalDistance / 1000);
      if (targetNode.position.y < sourceNode.position.y) {
        curvature *= -1;
      }
    }

    return {
      ...edge,
      edgeType,
      curvature,
      animated: false,
      style: {
        ...edge.style,
        strokeLinejoin: 'round',
        strokeLinecap: 'round',
      },
    };
  });
};

/**
 * Auto layout that prefers react-flow's live measurements over fallback dims.
 *
 * Three modes:
 *   1. All target nodes already measured in react-flow's internal store →
 *      one pass with real dims, no flicker.
 *   2. No reactFlowInstance available → one pass with fallbacks (best effort).
 *   3. Some nodes unmeasured (typical for fresh AI-gen / paste) → pass 1 with
 *      fallbacks so react-flow mounts and ResizeObserver fires, poll until
 *      every node has measured dims, then pass 2 with real dims.
 *
 * @param {Array} nodesItems
 * @param {Array} edges
 * @param {Object} reactFlowInstance - from useReactFlow() / ReactFlow onInit
 * @param {Object} [options] - forwarded to applyDagreLayouting
 * @returns {Promise<Array>}
 */
export const runTwoPassLayout = async (nodesItems, edges, reactFlowInstance, options = {}) => {
  const liveDimsById = readLiveDims(reactFlowInstance);
  const merged = mergeLiveDims(nodesItems, liveDimsById);

  const allMeasured = merged.every(n => n.type === NOTE_NODE_TYPE || hasLiveDims(n));
  if (allMeasured || !reactFlowInstance?.getNodes) {
    const result = applyDagreLayouting(merged, edges, options);
    useCanvasStore.getState().setNodesAndEdges(result);
    return result;
  }

  // Pass 1: rough layout so react-flow mounts the fresh nodes and measures them.
  const pass1 = applyDagreLayouting(merged, edges, options);
  useCanvasStore.getState().setNodesAndEdges(pass1);

  await waitForMeasurement(reactFlowInstance, nodesItems);

  // Pass 2: re-layout with the real measured dims now available.
  const measuredDimsById = readLiveDims(reactFlowInstance);
  const remeasured = mergeLiveDims(nodesItems, measuredDimsById);

  const pass2 = applyDagreLayouting(remeasured, edges, options);
  useCanvasStore.getState().setNodesAndEdges(pass2);
  return pass2;
};

const readLiveDims = reactFlowInstance => {
  if (!reactFlowInstance?.getNodes) return new Map();
  return new Map(
    reactFlowInstance
      .getNodes()
      .filter(hasLiveDims)
      .map(n => [n.id, { width: n.width, height: n.height }])
  );
};

const mergeLiveDims = (nodesItems, dimsById) =>
  nodesItems.map(node => {
    const dims = dimsById.get(node.id);
    return dims ? { ...node, width: dims.width, height: dims.height } : node;
  });

// Poll each animation frame until every non-note node has measured dims, or
// the timeout expires. ResizeObserver typically fires within 1-3 frames after
// mount; the cap prevents hanging on a node that never measures.
const waitForMeasurement = (reactFlowInstance, nodesItems, timeoutMs = 1500) =>
  new Promise(resolve => {
    const targetIds = new Set(
      nodesItems.filter(n => n.type !== NOTE_NODE_TYPE).map(n => n.id)
    );
    if (targetIds.size === 0) {
      resolve();
      return;
    }
    const start = performance.now();
    const tick = () => {
      const live = reactFlowInstance.getNodes();
      let measuredCount = 0;
      for (const n of live) {
        if (targetIds.has(n.id) && hasLiveDims(n)) measuredCount++;
      }
      if (measuredCount === targetIds.size) {
        resolve();
        return;
      }
      if (performance.now() - start >= timeoutMs) {
        resolve();
        return;
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });

const resolveLayoutWidth = node => {
  if (hasLiveDims(node)) return node.width;
  return DAGRE_DEFAULT_NODE_WIDTH;
};

const resolveLayoutHeight = node => {
  let baseHeight;
  if (hasLiveDims(node)) {
    baseHeight = node.height + NODE_HEIGHT_PADDING;
  } else {
    // No measurement available yet: estimate based on content.
    let estimated = DAGRE_DEFAULT_NODE_HEIGHT;
    if (node?.data?.description) estimated += 60;
    if (node?.data?.instructions) estimated += 60;
    baseHeight = estimated;
  }
  // Floor so collapsed nodes (~80px) still reserve room for expansion.
  return Math.max(baseHeight, MIN_LAYOUT_NODE_HEIGHT);
};
